/**
 * Service for managing focus states and accessibility navigation.
 * Provides utilities for focus trapping, restoration, and keyboard navigation.
 */

import { Injectable, ElementRef } from '@angular/core';

/**
 * Service for managing focus states and accessibility navigation.
 *
 * @remarks Provides utilities for managing focus in complex UI components
 * such as modals, menus, and other interactive elements. Includes focus trapping,
 * focus restoration, and keyboard navigation helpers for accessibility compliance.
 */
@Injectable({
  providedIn: 'root',
})
export class FocusManagementService {
  /** @private Reference to the last focused element before focus management began */
  private lastFocusedElement: HTMLElement | null = null;

  /**
   * Save the currently focused element.
   *
   * @remarks Stores a reference to the currently active element so that
   * focus can be restored later. Typically used before opening modals or
   * other components that manage focus.
   */
  saveCurrentFocus(): void {
    this.lastFocusedElement = document.activeElement as HTMLElement;
  }

  /**
   * Restore focus to the previously saved element.
   *
   * @remarks Returns focus to the element that was active before focus
   * management began. Uses setTimeout to ensure the element is available
   * and ready to receive focus.
   */
  restoreFocus(): void {
    setTimeout(() => {
      if (this.lastFocusedElement) {
        this.lastFocusedElement.focus();
      }
    }, 0);
  }

  /**
   * Get focusable elements within a container.
   *
   * @remarks Finds all focusable elements within a given container element.
   * Returns the first, last, and all focusable elements for use in focus management.
   * Includes common interactive elements like links, buttons, form controls, etc.
   *
   * @param {ElementRef} container - The container element to search within
   * @returns {{ first: HTMLElement | null; last: HTMLElement | null; all: HTMLElement[] }} Object containing focusable elements
   */
  getFocusableElements(container: ElementRef): {
    first: HTMLElement | null;
    last: HTMLElement | null;
    all: HTMLElement[];
  } {
    if (!container?.nativeElement) {
      return { first: null, last: null, all: [] };
    }

    const selector =
      'a[href], button:not([disabled]), textarea:not([disabled]), ' +
      'input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), ' +
      'input[type="checkbox"]:not([disabled]), select:not([disabled])';

    const elements = Array.from(container.nativeElement.querySelectorAll(selector)) as HTMLElement[];

    return {
      first: elements.length > 0 ? elements[0] : null,
      last: elements.length > 0 ? elements[elements.length - 1] : null,
      all: elements,
    };
  }

  /**
   * Focus the first focusable element in a container.
   *
   * @remarks Automatically focuses the first interactive element within
   * a container. Uses setTimeout to ensure the element is rendered and ready
   * to receive focus. Commonly used when opening modals or navigation menus.
   *
   * @param {ElementRef} container - The container element to search within
   */
  focusFirstElement(container: ElementRef): void {
    setTimeout(() => {
      const { first } = this.getFocusableElements(container);
      if (first) {
        first.focus();
      }
    }, 100);
  }

  /**
   * Handle tab key navigation for modal-like components to trap focus within a container.
   *
   * @remarks Manages keyboard navigation within a focus trap by handling
   * Tab and Shift+Tab key presses. When the user tries to tab beyond the
   * boundaries of the focusable area, calls the provided exit handler.
   * Essential for modal dialogs and other overlay components.
   *
   * @param {KeyboardEvent} event - The keyboard event to handle
   * @param {HTMLElement | null} firstElement - The first focusable element in the container
   * @param {HTMLElement | null} lastElement - The last focusable element in the container
   * @param {() => void} onExit - Callback function to execute when focus should exit the container
   */
  handleTabKey(event: KeyboardEvent, firstElement: HTMLElement | null, lastElement: HTMLElement | null, onExit: () => void): void {
    if (!firstElement || !lastElement) {
      return;
    }

    // If shift+tab on first element or tab on last element, call exit handler
    if ((event.shiftKey && document.activeElement === firstElement) || (!event.shiftKey && document.activeElement === lastElement)) {
      onExit();
      event.preventDefault();
    }
  }
}
